import React, { useState, useEffect } from 'react';
import Layout from '@/components/Layout';
import { Package, Plus, Sprout, Bell, ShoppingCart, Trash2, X, CheckCircle2, AlertTriangle, Heart, ArrowRight, ScanLine } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VIABILITY_STYLE = {
  fresh: { color: '#6B9F4A', bg: 'bg-[#6B9F4A]/10 border-[#6B9F4A]/30' },
  good: { color: '#6B9F4A', bg: 'bg-[#6B9F4A]/10 border-[#6B9F4A]/30' },
  aging: { color: '#C9A85B', bg: 'bg-[#C9A85B]/10 border-[#C9A85B]/30' },
  old: { color: '#E2725B', bg: 'bg-[#E2725B]/10 border-[#E2725B]/30' },
  unknown: { color: '#8EAA97', bg: 'bg-[#F0F2EB] border-[#8EAA97]/30' },
};

function SeedBox({ user }) {
  const [tab, setTab] = useState('inventory');
  const [seeds, setSeeds] = useState([]);
  const [reorderCount, setReorderCount] = useState(0);
  const [showAdd, setShowAdd] = useState(false);
  const [sowSeed, setSowSeed] = useState(null);
  const [reminderState, setReminderState] = useState('idle');
  const [addError, setAddError] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [form, setForm] = useState({ crop_name: '', variety: '', supplier: '', date_bought: '', initial_seeds: '', notes: '' });

  const [wishlist, setWishlist] = useState([]);
  const [showAddWish, setShowAddWish] = useState(false);
  const [wishForm, setWishForm] = useState({ crop_name: '', variety: '', supplier: '', notes: '' });
  const [acquireItem, setAcquireItem] = useState(null);
  
  const [seasonalNudges, setSeasonalNudges] = useState([]);

  const fetchSeeds = async () => {
    try {
      const res = await axios.get(`${API}/seeds`, { withCredentials: true });
      setSeeds(res.data.seeds || []);
      setReorderCount(res.data.reorder_count || 0);
    } catch (e) { console.error(e); }
  };

  const fetchWishlist = async () => {
    try {
      const res = await axios.get(`${API}/seeds/wishlist`, { withCredentials: true });
      setWishlist(res.data.wishlist || []);
      // Fetch seasonal nudges
      const nudgesRes = await axios.get(`${API}/seeds/seasonal-nudges`, { withCredentials: true });
      setSeasonalNudges(nudgesRes.data.nudges || []);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchSeeds(); fetchWishlist(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    setAddError('');
    try {
      await axios.post(`${API}/seeds`, form, { withCredentials: true });
      setShowAdd(false);
      setForm({ crop_name: '', variety: '', supplier: '', date_bought: '', initial_seeds: '', notes: '' });
      fetchSeeds();
    } catch (e) {
      console.error(e);
      setAddError(e.response?.data?.detail || 'Could not save — please try again.');
    }
  };

  const handleDelete = async (id) => {
    try { await axios.delete(`${API}/seeds/${id}`, { withCredentials: true }); setConfirmDelete(null); fetchSeeds(); }
    catch (e) { console.error(e); }
  };

  const handleRemindAll = async () => {
    setReminderState('loading');
    try {
      await axios.post(`${API}/seeds/reorder-reminders`, {}, { withCredentials: true });
      setReminderState('done');
    } catch (e) { console.error(e); setReminderState('idle'); }
  };

  const handleAddWish = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/seeds/wishlist`, wishForm, { withCredentials: true });
      setShowAddWish(false);
      setWishForm({ crop_name: '', variety: '', supplier: '', notes: '' });
      fetchWishlist();
    } catch (e) { console.error(e); }
  };

  const handleDeleteWish = async (id) => {
    try { await axios.delete(`${API}/seeds/wishlist/${id}`, { withCredentials: true }); fetchWishlist(); }
    catch (e) { console.error(e); }
  };

  const reorderSeeds = seeds.filter(s => s.needs_reorder);

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl sm:text-5xl font-bold text-[#1A4331] tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>Seed Box</h1>
            <p className="text-base text-[#5C665C] mt-2">Track your seed packets, freshness and when to reorder</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/seed-scanner" data-testid="seed-scan-entry" className="bg-white border-2 border-[#1A4331] text-[#1A4331] hover:bg-[#1A4331]/5 font-semibold py-3 px-5 rounded-full transition-colors flex items-center gap-2"><ScanLine className="w-5 h-5" />Scan packet</Link>
            {tab === 'inventory'
              ? <button onClick={() => setShowAdd(true)} data-testid="add-seed-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2"><Plus className="w-5 h-5" />Add seeds</button>
              : <button onClick={() => setShowAddWish(true)} data-testid="add-wish-button" className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full transition-colors flex items-center gap-2"><Plus className="w-5 h-5" />Add to wishlist</button>}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 border-b border-[#1A4331]/10">
          <button onClick={() => setTab('inventory')} data-testid="tab-inventory" className={`px-4 py-2.5 font-semibold text-sm border-b-2 transition-colors ${tab === 'inventory' ? 'border-[#1A4331] text-[#1A4331]' : 'border-transparent text-[#5C665C] hover:text-[#1A4331]'}`}>
            My Seeds{seeds.length > 0 ? ` (${seeds.length})` : ''}
          </button>
          <button onClick={() => setTab('wishlist')} data-testid="tab-wishlist" className={`px-4 py-2.5 font-semibold text-sm border-b-2 transition-colors flex items-center gap-1.5 ${tab === 'wishlist' ? 'border-[#1A4331] text-[#1A4331]' : 'border-transparent text-[#5C665C] hover:text-[#1A4331]'}`}>
            <Heart className="w-4 h-4" />Wishlist{wishlist.length > 0 ? ` (${wishlist.length})` : ''}
          </button>
        </div>

        {tab === 'inventory' && (
          <>
            {reorderCount > 0 && (
              <div className="bg-gradient-to-br from-[#E2725B]/10 to-[#C9A85B]/10 rounded-3xl p-6 border border-[#E2725B]/20" data-testid="reorder-banner">
                <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-6 h-6 text-[#E2725B]" />
                    <h2 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>{reorderCount} packet{reorderCount > 1 ? 's' : ''} to reorder</h2>
                  </div>
                  <button onClick={handleRemindAll} disabled={reminderState !== 'idle'} data-testid="remind-reorder-button"
                    className="bg-[#1A4331] hover:bg-[#255C44] text-white text-sm font-semibold py-2.5 px-5 rounded-full transition-colors flex items-center gap-2 disabled:opacity-60">
                    {reminderState === 'done' ? <><CheckCircle2 className="w-4 h-4" /> Reminders set</> : <><Bell className="w-4 h-4" /> Remind me</>}
                  </button>
                </div>
                <div className="space-y-2">
                  {reorderSeeds.map(s => (
                    <div key={s.seed_id} className="flex items-center justify-between flex-wrap gap-2 bg-white/60 rounded-xl px-4 py-2.5" data-testid={`reorder-item-${s.seed_id}`}>
                      <div>
                        <span className="font-semibold text-[#2D241E]">{s.crop_name}{s.variety ? ` · ${s.variety}` : ''}</span>
                        <span className="text-sm text-[#5C665C] ml-2">{s.reorder_reasons.join(' · ')}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(s.suppliers || []).map(sup => (
                          <a key={sup.name} href={sup.url} target="_blank" rel="noopener noreferrer" data-testid={`buy-link-${s.seed_id}`}
                            className="text-xs font-semibold text-[#1A4331] bg-white border border-[#8EAA97]/50 hover:border-[#1A4331] px-3 py-1.5 rounded-full transition-colors flex items-center gap-1">
                            <ShoppingCart className="w-3 h-3" />{sup.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {seeds.length === 0 ? (
              <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center" data-testid="seed-empty-state">
                <Package className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-[#2D241E] mb-2">Your Seed Box is empty</h3>
                <p className="text-sm text-[#5C665C] mb-6">Add your seed packets to track freshness and get reorder reminders.</p>
                <button onClick={() => setShowAdd(true)} className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full inline-flex items-center gap-2">
                  <Plus className="w-5 h-5" />Add your first packet
                </button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="seed-grid">
                {seeds.map(s => {
                  const vs = VIABILITY_STYLE[s.viability_status] || VIABILITY_STYLE.unknown;
                  return (
                    <div key={s.seed_id} className="bg-white rounded-3xl p-5 border border-[#1A4331]/5 shadow-sm flex flex-col" data-testid={`seed-card-${s.seed_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-[#1A4331] text-lg">{s.crop_name}</h3>
                          {s.variety && <p className="text-sm text-[#5C665C]">{s.variety}</p>}
                        </div>
                        <button onClick={() => setConfirmDelete(s)} data-testid={`delete-seed-${s.seed_id}`} className="text-[#8EAA97] hover:text-[#E2725B] transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <span className={`text-xs font-bold px-2.5 py-1 rounded-full border ${vs.bg}`} style={{ color: vs.color }} data-testid={`viability-${s.seed_id}`}>{s.viability_label}</span>
                        {s.germination_estimate != null && (
                          <span className="text-xs font-medium text-[#5C665C]">~{s.germination_estimate}% germination</span>
                        )}
                      </div>

                      <div className="mt-3 text-sm text-[#5C665C] space-y-1">
                        {s.supplier && <p>Supplier: <span className="text-[#2D241E] font-medium">{s.supplier}</span></p>}
                        <p>{s.age_text}</p>
                      </div>

                      {s.remaining_seeds != null && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-[#5C665C] mb-1">
                            <span>Seeds left</span>
                            <span className="font-semibold text-[#1A4331]" data-testid={`remaining-${s.seed_id}`}>
                              {s.remaining_seeds}{s.initial_seeds ? ` / ${s.initial_seeds}` : ''}
                            </span>
                          </div>
                          {s.remaining_pct != null && (
                            <div className="h-2 rounded-full bg-[#F0F2EB] overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${s.remaining_pct}%`, backgroundColor: s.stock_status === 'ok' ? '#6B9F4A' : '#E2725B' }} />
                            </div>
                          )}
                        </div>
                      )}

                      <div className="mt-4 pt-3 border-t border-[#1A4331]/5 flex items-center gap-3">
                        <button onClick={() => setSowSeed(s)} data-testid={`sow-seed-${s.seed_id}`}
                          className="flex-1 bg-[#F0F2EB] hover:bg-[#8EAA97]/20 text-[#1A4331] font-semibold py-2 rounded-full transition-colors flex items-center justify-center gap-2 text-sm">
                          <Sprout className="w-4 h-4" />Sow
                        </button>
                        {s.needs_reorder && (s.suppliers || []).length > 0 && (
                          <a href={s.suppliers[0].url} target="_blank" rel="noopener noreferrer" data-testid={`card-buy-${s.seed_id}`}
                            className="flex-1 bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-2 rounded-full transition-colors flex items-center justify-center gap-2 text-sm">
                            <ShoppingCart className="w-4 h-4" />Buy again
                          </a>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {tab === 'wishlist' && (
          <>
            {/* Seasonal Nudges Banner */}
            {seasonalNudges.length > 0 && (
              <div className="bg-gradient-to-br from-[#6B9F4A]/10 to-[#B4D330]/10 rounded-3xl p-6 border border-[#6B9F4A]/20 mb-6" data-testid="seasonal-nudges">
                <div className="flex items-center gap-3 mb-4">
                  <Sprout className="w-6 h-6 text-[#6B9F4A]" />
                  <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    🌱 Time to sow!
                  </h3>
                </div>
                <p className="text-sm text-[#5C665C] mb-4">
                  These seeds from your wishlist are in their ideal planting window right now.
                </p>
                <div className="space-y-3">
                  {seasonalNudges.map((nudge) => (
                    <div key={nudge.wishlist_id} className="bg-white/60 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid={`nudge-${nudge.wishlist_id}`}>
                      <div className="flex-1">
                        <p className="font-semibold text-[#1A4331]">
                          {nudge.crop_name}{nudge.variety ? ` · ${nudge.variety}` : ''}
                        </p>
                        <p className="text-sm text-[#5C665C]">{nudge.message}</p>
                      </div>
                      {nudge.action === 'plant' && nudge.seed_id ? (
                        <button
                          onClick={() => {
                            const seed = seeds.find(s => s.seed_id === nudge.seed_id);
                            if (seed) setSowSeed(seed);
                          }}
                          className="bg-[#6B9F4A] hover:bg-[#5A8539] text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center gap-2 text-sm"
                        >
                          <Sprout className="w-4 h-4" />
                          Plant now
                        </button>
                      ) : (
                        <button
                          onClick={() => {
                            const item = wishlist.find(w => w.wishlist_id === nudge.wishlist_id);
                            if (item) setAcquireItem(item);
                          }}
                          className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-2 px-4 rounded-full transition-colors flex items-center gap-2 text-sm"
                        >
                          <ShoppingCart className="w-4 h-4" />
                          Get seeds
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {wishlist.length === 0 ? (
            <div className="bg-white rounded-3xl p-12 border border-[#1A4331]/5 shadow-sm text-center" data-testid="wishlist-empty-state">
              <Heart className="w-16 h-16 text-[#8EAA97] mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#2D241E] mb-2">Your wishlist is empty</h3>
              <p className="text-sm text-[#5C665C] mb-6">Save seeds you want to try next season, then order them all in one trip.</p>
              <button onClick={() => setShowAddWish(true)} className="bg-[#1A4331] hover:bg-[#255C44] text-white font-semibold py-3 px-6 rounded-full inline-flex items-center gap-2">
                <Plus className="w-5 h-5" />Add to wishlist
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="wishlist-grid">
              {wishlist.map(w => (
                <div key={w.wishlist_id} className="bg-white rounded-3xl p-5 border border-[#1A4331]/5 shadow-sm flex flex-col" data-testid={`wish-card-${w.wishlist_id}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-semibold text-[#1A4331] text-lg">{w.crop_name}</h3>
                      {w.variety && <p className="text-sm text-[#5C665C]">{w.variety}</p>}
                      {w.supplier && <p className="text-xs text-[#5C665C] mt-1">Prefer: {w.supplier}</p>}
                      {w.notes && <p className="text-sm text-[#5C665C] mt-2 italic">{w.notes}</p>}
                    </div>
                    <button onClick={() => handleDeleteWish(w.wishlist_id)} data-testid={`delete-wish-${w.wishlist_id}`} className="text-[#8EAA97] hover:text-[#E2725B] transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {(w.suppliers || []).map(sup => (
                      <a key={sup.name} href={sup.url} target="_blank" rel="noopener noreferrer" data-testid={`wish-buy-${w.wishlist_id}`}
                        className="text-xs font-semibold text-[#1A4331] bg-[#F0F2EB] hover:bg-[#8EAA97]/20 px-3 py-1.5 rounded-full transition-colors flex items-center gap-1">
                        <ShoppingCart className="w-3 h-3" />{sup.name}
                      </a>
                    ))}
                  </div>
                  <button onClick={() => setAcquireItem(w)} data-testid={`acquire-wish-${w.wishlist_id}`}
                    className="mt-4 pt-3 border-t border-[#1A4331]/5 text-[#1A4331] font-semibold text-sm flex items-center justify-center gap-2 hover:text-[#255C44] transition-colors">
                    Got it — move to Seed Box <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          )}
          </>
        )}
      </div>

      {/* Add seed modal */}
      {showAdd && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowAdd(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="add-seed-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Add seeds</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-[#5C665C]" /></button>
            </div>
            <form onSubmit={handleAdd} className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Crop *</label>
                <input required value={form.crop_name} onChange={e => setForm({ ...form, crop_name: e.target.value })} placeholder="e.g., Carrot" data-testid="seed-crop-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Variety</label>
                <input value={form.variety} onChange={e => setForm({ ...form, variety: e.target.value })} placeholder="e.g., Nantes" data-testid="seed-variety-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Supplier</label>
                <input value={form.supplier} onChange={e => setForm({ ...form, supplier: e.target.value })} placeholder="e.g., Kings Seeds" data-testid="seed-supplier-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Date bought</label>
                <input type="date" value={form.date_bought} onChange={e => setForm({ ...form, date_bought: e.target.value })} data-testid="seed-date-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Seeds in packet</label>
                <input type="number" min="0" value={form.initial_seeds} onChange={e => setForm({ ...form, initial_seeds: e.target.value })} placeholder="e.g., 200" data-testid="seed-count-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div className="col-span-2">
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Notes</label>
                <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="seed-notes-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none resize-none" />
              </div>
              <div className="col-span-2 flex gap-3">
                <button type="submit" data-testid="save-seed-button" className="bg-[#1A4331] text-white font-semibold py-3 px-6 rounded-full">Add to Seed Box</button>
                <button type="button" onClick={() => setShowAdd(false)} className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
              </div>
              {addError && <p className="col-span-2 text-sm text-[#E2725B]" data-testid="add-seed-error">{addError}</p>}
            </form>
          </div>
        </div>
      )}

      {/* Add wishlist modal */}
      {showAddWish && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setShowAddWish(false)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-lg" onClick={e => e.stopPropagation()} data-testid="add-wish-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Add to wishlist</h3>
              <button onClick={() => setShowAddWish(false)}><X className="w-5 h-5 text-[#5C665C]" /></button>
            </div>
            <form onSubmit={handleAddWish} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Crop *</label>
                <input required value={wishForm.crop_name} onChange={e => setWishForm({ ...wishForm, crop_name: e.target.value })} placeholder="e.g., Purple Sprouting Broccoli" data-testid="wish-crop-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#2D241E] mb-1">Variety</label>
                  <input value={wishForm.variety} onChange={e => setWishForm({ ...wishForm, variety: e.target.value })} data-testid="wish-variety-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#2D241E] mb-1">Preferred supplier</label>
                  <input value={wishForm.supplier} onChange={e => setWishForm({ ...wishForm, supplier: e.target.value })} data-testid="wish-supplier-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-[#2D241E] mb-1">Notes</label>
                <textarea value={wishForm.notes} onChange={e => setWishForm({ ...wishForm, notes: e.target.value })} rows={2} data-testid="wish-notes-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none resize-none" />
              </div>
              <div className="flex gap-3">
                <button type="submit" data-testid="save-wish-button" className="bg-[#1A4331] text-white font-semibold py-3 px-6 rounded-full">Add to wishlist</button>
                <button type="button" onClick={() => setShowAddWish(false)} className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={() => setConfirmDelete(null)}>
          <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="delete-confirm-modal">
            <h3 className="text-xl font-semibold text-[#1A4331] mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Delete this packet?</h3>
            <p className="text-sm text-[#5C665C] mb-5">Remove <span className="font-semibold text-[#2D241E]">{confirmDelete.crop_name}{confirmDelete.variety ? ` · ${confirmDelete.variety}` : ''}</span> from your Seed Box? This can&apos;t be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => handleDelete(confirmDelete.seed_id)} data-testid="confirm-delete-button" className="bg-[#E2725B] hover:bg-[#c95f49] text-white font-semibold py-3 px-6 rounded-full transition-colors">Delete</button>
              <button onClick={() => setConfirmDelete(null)} data-testid="cancel-delete-button" className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Sow modal */}
      {sowSeed && <SowModal seed={sowSeed} onClose={() => setSowSeed(null)} onDone={() => { setSowSeed(null); fetchSeeds(); }} />}

      {/* Acquire (move wishlist -> seed box) modal */}
      {acquireItem && <AcquireModal item={acquireItem} onClose={() => setAcquireItem(null)} onDone={() => { setAcquireItem(null); fetchWishlist(); fetchSeeds(); setTab('inventory'); }} />}
    </Layout>
  );
}

function SowModal({ seed, onClose, onDone }) {
  const [used, setUsed] = useState('');
  const [packetSize, setPacketSize] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const needsStart = seed.remaining_seeds == null;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (needsStart && (!packetSize || Number(packetSize) <= 0)) {
      setError('Please enter how many seeds the packet started with.');
      return;
    }
    setSaving(true);
    try {
      const body = { seeds_used: Number(used) };
      if (needsStart) body.packet_size = Number(packetSize);
      await axios.post(`${API}/seeds/${seed.seed_id}/sow`, body, { withCredentials: true });
      onDone();
    } catch (e) {
      console.error(e);
      setError(e.response?.data?.detail || 'Could not record — please try again.');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="sow-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Sow {seed.crop_name}</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5C665C]" /></button>
        </div>
        <p className="text-sm text-[#5C665C] mb-4">We&apos;ll subtract these from your packet.</p>
        <form onSubmit={submit} className="space-y-4">
          {needsStart && (
            <div>
              <label className="block text-sm font-semibold text-[#2D241E] mb-1">Seeds in packet at the start *</label>
              <input required type="number" min="1" value={packetSize} onChange={e => setPacketSize(e.target.value)} placeholder="e.g., 200" data-testid="sow-packet-size-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
            </div>
          )}
          <div>
            <label className="block text-sm font-semibold text-[#2D241E] mb-1">Seeds used</label>
            <input required type="number" min="1" value={used} onChange={e => setUsed(e.target.value)} placeholder="e.g., 20" data-testid="sow-used-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
            {seed.remaining_seeds != null && <p className="text-xs text-[#5C665C] mt-1">{seed.remaining_seeds} seeds currently left</p>}
          </div>
          {error && <p className="text-sm text-[#E2725B]" data-testid="sow-error">{error}</p>}
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="confirm-sow-button" className="bg-[#1A4331] text-white font-semibold py-3 px-6 rounded-full disabled:opacity-60">Sow seeds</button>
            <button type="button" onClick={onClose} className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AcquireModal({ item, onClose, onDone }) {
  const [initial, setInitial] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await axios.post(`${API}/seeds/wishlist/${item.wishlist_id}/acquire`, { initial_seeds: initial || null }, { withCredentials: true });
      onDone();
    } catch (e) { console.error(e); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[60] p-4" onClick={onClose}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()} data-testid="acquire-modal">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-xl font-semibold text-[#1A4331]" style={{ fontFamily: 'Outfit, sans-serif' }}>Move to Seed Box</h3>
          <button onClick={onClose}><X className="w-5 h-5 text-[#5C665C]" /></button>
        </div>
        <p className="text-sm text-[#5C665C] mb-4">Adding <span className="font-semibold text-[#2D241E]">{item.crop_name}{item.variety ? ` · ${item.variety}` : ''}</span> to your inventory, bought today.</p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-[#2D241E] mb-1">Seeds in packet (optional)</label>
            <input type="number" min="0" value={initial} onChange={e => setInitial(e.target.value)} placeholder="e.g., 200" data-testid="acquire-count-input" className="w-full px-4 py-3 bg-[#F0F2EB] rounded-xl outline-none" />
          </div>
          <div className="flex gap-3">
            <button type="submit" disabled={saving} data-testid="confirm-acquire-button" className="bg-[#1A4331] text-white font-semibold py-3 px-6 rounded-full disabled:opacity-60">Add to Seed Box</button>
            <button type="button" onClick={onClose} className="border-2 border-[#8EAA97] text-[#1A4331] font-semibold py-3 px-6 rounded-full">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default SeedBox;
